import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../store/useSessionStore";
import { getTerms } from "../services/api";

/**
 * Keeps the claimed session's Terms & Conditions block in sync with the active
 * UI language. The backend serves manually authored per-language content
 * (English fallback), so whenever the user switches language we refetch and
 * merge the localized terms into the session. The embedded claim terms are used
 * until the first fetch resolves.
 */
export function useTerms() {
  const { i18n } = useTranslation();
  const linkId = useSessionStore((s) => s.session.data?.linkId);
  const updateSession = useSessionStore((s) => s.updateSession);
  const lang = i18n.language;

  useEffect(() => {
    if (!linkId) return;
    let cancelled = false;

    (async () => {
      try {
        const terms = await getTerms(linkId, lang);
        if (cancelled || !terms) return;

        const current = useSessionStore.getState().session.data;
        if (!current) return;

        if (
          current.terms?.version === terms.version &&
          current.terms?.language === terms.language
        ) {
          return;
        }

        updateSession({ ...current, terms });
      } catch {
        // Keep whatever terms are already in the session on failure.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [linkId, lang, updateSession]);
}
